import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

function MainHeader() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const handleDropdownEnter = (name) => setActiveDropdown(name);
  const handleDropdownLeave = () => setActiveDropdown(null);
  const isActive = (path) => location.pathname === path;

  const navigation = [
    { 
      label: 'Free Tools', 
      type: 'dropdown', 
      name: 'tools',
      items: [
        { label: 'AI Accountability Score', path: '/assessment/form' },
        { label: 'AI Policy Generator', path: '/tools/policy-generator' },
        { label: 'AI Tool Inventory Builder', path: '/tools/inventory-builder' },
        { label: 'AI Governance Checklist', path: '/tools/governance-checklist' },
        { divider: true },
        { label: 'See All Free Tools', path: '/free-tools' }
      ]
    },
    { 
      label: 'Standards', 
      type: 'dropdown', 
      name: 'standards',
      items: [
        { label: 'Standards Index', path: '/standards' },
        { label: 'Framework Alignment', path: '/framework-alignment' },
        { label: 'Standards Taxonomy', path: '/standards-taxonomy' },
        { divider: true },
        { label: 'Foundational Standards', header: true },
        { label: 'AI-ABND-1.0: Accountability Boundaries', path: '/standards/ai-abnd-1-0' },
        { label: 'AI-HITL-1.0: Human Authority', path: '/standards/ai-hitl-1-0' },
        { label: 'AI-ACCT-1.0: Accountability Mapping', path: '/standards/ai-acct-1-0' },
        { label: 'AI-READ-1.0: Readiness & Response', path: '/standards/ai-read-1-0' },
        { label: 'AI-PERF-1.0: Performance Control', path: '/standards/ai-perf-1-0' },
        { label: 'AI-ROI-1.0: Domain Definition', path: '/standards/ai-roi-1-0' },
        { label: 'AI-DISC-1.0: Client Disclosure', path: '/standards/ai-disc-1-0' },
        { divider: true },
        { label: 'Archived Standards', path: '/archived-standards' }
      ]
    },
    { 
      label: 'Registry', 
      type: 'dropdown', 
      name: 'registry',
      items: [
        { label: 'Public Registry', path: '/registry' },
        { label: 'Verify Declaration Status', path: '/verify-credential' },
        { label: 'Registry Status Definitions', path: '/registry-status-definitions' },
        { label: 'Submit a Concern', path: '/submit-a-concern' },
        { divider: true },
        { label: 'Declaration Badge Usage', path: '/declaration-badge-usage-policy' }
      ]
    },
    { 
      label: 'Governance', 
      type: 'dropdown',
      name: 'governance',
      items: [
        { label: 'Governance & Authority', path: '/governance-and-authority' },
        { label: 'Independence Statement', path: '/independence-statement' },
        { label: 'Conflict of Interest Policy', path: '/conflict-of-interest-policy' },
        { label: 'Scope of Applicability', path: '/scope-of-applicability' },
        { label: 'Suspension & Revocation', path: '/suspension-and-revocation-policy' }
      ]
    },
    { label: 'About', path: '/about', type: 'link' },
    { label: 'Articles', path: '/articles', type: 'link' },
  ];

  return (
    <header className="bg-white border-b border-zinc-200 relative z-50">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <nav className="flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-zinc-900 whitespace-nowrap z-50" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            AI Accountability Standards Council
          </Link>
          
          <div className="hidden lg:flex items-center gap-5">
            {navigation.map((nav) => (
              nav.type === 'link' ? (
                <Link 
                  key={nav.path} 
                  to={nav.path} 
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${isActive(nav.path) ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'}`}
                >
                  {nav.label}
                </Link>
              ) : (
                <div 
                  key={nav.name} 
                  className="relative group h-full flex items-center" 
                  onMouseEnter={() => handleDropdownEnter(nav.name)} 
                  onMouseLeave={handleDropdownLeave}
                >
                  <button className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${activeDropdown === nav.name ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'}`}>
                    {nav.label}
                    <ChevronDown size={14} />
                  </button>
                  
                  {activeDropdown === nav.name && (
                    <div className="absolute top-full left-0 pt-2 w-72">
                      <div className="bg-white border border-zinc-200 shadow-lg py-2 max-h-[80vh] overflow-y-auto">
                        {nav.items.map((item, idx) => {
                          if (item.divider) return <div key={idx} className="border-t border-zinc-100 my-1 mx-3"></div>;
                          if (item.header) return <div key={idx} className="px-4 py-2 text-xs font-semibold text-zinc-400 uppercase tracking-wide">{item.label}</div>;
                          return (
                            <Link 
                              key={item.path} 
                              to={item.path} 
                              className={`block px-4 py-2 text-sm hover:bg-zinc-50 hover:text-zinc-900 ${isActive(item.path) ? 'text-blue-600 font-medium' : 'text-zinc-700'}`}
                            >
                              {item.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            ))}
            <Link 
              to="/assessment/form" 
              className="ml-4 text-sm font-semibold bg-[#2F80ED] text-white rounded-md px-5 py-2 hover:bg-[#2563eb] transition-colors"
            >
              Get Your Free Score
            </Link>
          </div>

          <button className="lg:hidden p-2 text-zinc-600 z-50" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-zinc-200 shadow-lg py-6 px-6 flex flex-col gap-6 max-h-[80vh] overflow-y-auto">
          {navigation.map((nav) => (
            nav.type === 'link' ? (
              <Link 
                key={nav.path} 
                to={nav.path} 
                className="text-base font-semibold text-zinc-900" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {nav.label}
              </Link>
            ) : (
              <div key={nav.name}>
                <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-3">{nav.label}</div>
                <div className="flex flex-col gap-2 pl-4 border-l-2 border-zinc-100">
                  {nav.items.map((item, idx) => (
                    !item.divider && !item.header && (
                      <Link 
                        key={item.path} 
                        to={item.path} 
                        className="text-sm text-zinc-700" 
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )
          ))}
          <Link 
            to="/assessment/form" 
            className="mt-4 text-center font-semibold bg-[#2F80ED] text-white rounded-md px-5 py-3 hover:bg-[#2563eb] transition-colors"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Get Your Free Score
          </Link>
        </div>
      )}
    </header>
  );
}

export default MainHeader;
